import React, { useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/apiClient";
import { useSettings } from "@/context/SettingsContext";
import SEO from "@/components/SEO";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CATS = ["General", "Legal / PIL Enquiry", "Media", "Representation", "Grievance", "Other"];

export default function Contact() {
  const s = useSettings();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", category: "General", message: "", consent: false });
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) return toast.error("Please provide consent to proceed.");
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success("Your enquiry has been received. We will respond in due course.");
      setForm({ name: "", email: "", phone: "", subject: "", category: "General", message: "", consent: false });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Submission failed");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fade-in">
      <SEO title="Contact — NPST" description="Contact the National Public Service Trust." canonical={`${window.location.origin}/contact`} />
      <section className="border-b border-npst-line paper-texture">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="label-eyebrow text-npst-gold mb-3">Public Communication</div>
          <h1 className="font-serif text-4xl sm:text-6xl font-semibold text-npst-ink tracking-tight">Contact the Trust</h1>
        </div>
      </section>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4 space-y-6">
          <div><div className="label-eyebrow text-npst-gold mb-1">Organisation</div><div className="font-serif text-2xl text-npst-ink">{s.org_name}</div></div>
          {s.address && <div><div className="label-eyebrow text-npst-gold mb-1">Registered Office</div><div className="text-npst-inkSoft">{s.address}</div></div>}
          {s.email && <div><div className="label-eyebrow text-npst-gold mb-1">Email</div><div className="text-npst-inkSoft">{s.email}</div></div>}
          {s.phone && <div><div className="label-eyebrow text-npst-gold mb-1">Telephone</div><div className="text-npst-inkSoft">{s.phone}</div></div>}
          {s.office_hours && <div><div className="label-eyebrow text-npst-gold mb-1">Office Hours</div><div className="text-npst-inkSoft">{s.office_hours}</div></div>}
        </div>
        <div className="lg:col-span-8">
          <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div><label className="label-eyebrow text-npst-muted block mb-2">Full Name</label><Input data-testid="contact-name" required value={form.name} onChange={(e) => set("name", e.target.value)} className="h-12 bg-white" /></div>
              <div><label className="label-eyebrow text-npst-muted block mb-2">Email</label><Input data-testid="contact-email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} className="h-12 bg-white" /></div>
              <div><label className="label-eyebrow text-npst-muted block mb-2">Phone</label><Input data-testid="contact-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="h-12 bg-white" /></div>
              <div>
                <label className="label-eyebrow text-npst-muted block mb-2">Category</label>
                <Select value={form.category} onValueChange={(v) => set("category", v)}>
                  <SelectTrigger data-testid="contact-category" className="h-12 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><label className="label-eyebrow text-npst-muted block mb-2">Subject</label><Input data-testid="contact-subject" required value={form.subject} onChange={(e) => set("subject", e.target.value)} className="h-12 bg-white" /></div>
            <div><label className="label-eyebrow text-npst-muted block mb-2">Message</label><Textarea data-testid="contact-message" required rows={6} value={form.message} onChange={(e) => set("message", e.target.value)} className="bg-white" /></div>
            <div className="flex items-start gap-3">
              <Checkbox id="consent" data-testid="contact-consent" checked={form.consent} onCheckedChange={(v) => set("consent", !!v)} className="mt-1" />
              <label htmlFor="consent" className="text-sm text-npst-inkSoft leading-relaxed">I consent to the Trust storing and processing the information above for the purpose of responding to my enquiry.</label>
            </div>
            <Button type="submit" disabled={submitting} data-testid="contact-submit" className="bg-npst-navy hover:bg-npst-navyDeep text-white px-10 h-12 uppercase tracking-[0.1em] text-sm">{submitting ? "Submitting…" : "Submit"}</Button>
          </form>
        </div>
      </section>
    </div>
  );
}
